import { useState, type ChangeEvent, type FormEvent } from "react";
import Button from "@/components/base/Button";
import { normalizeClubPhone, type ClubAccessMode } from '@/lib/clubIdentity';

export interface RegisterData {
  name: string;
  phone: string;
  email: string;
}

interface RegisterFormProps {
  onSubmit: (data: RegisterData) => Promise<void>;
  mode: ClubAccessMode;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function RegisterForm({ onSubmit, mode }: RegisterFormProps) {
  const [form, setForm] = useState<RegisterData>({ name: "", phone: "", email: "" });
  const [errors, setErrors] = useState<Partial<Record<keyof RegisterData, string>>>({});
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
    setSubmitError('');
  };

  const validate = (): boolean => {
    const next: Partial<Record<keyof RegisterData, string>> = {};
    if (form.name.trim().length < 2) next.name = "Escribe tu nombre";
    const digits = normalizeClubPhone(form.phone);
    if (digits.length !== 10) next.phone = "Escribe tu celular de 10 dígitos; también puedes usar +52";
    if (mode === 'register' && !EMAIL_RE.test(form.email.trim())) next.email = "Escribe un correo válido";
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (busy || !validate()) return;
    setBusy(true); setSubmitError('');
    try { await onSubmit({
      name: form.name.trim(),
      phone: normalizeClubPhone(form.phone),
      email: mode === 'register' ? form.email.trim() : '',
    }); } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'No pudimos completar el acceso. Intenta de nuevo.');
    } finally { setBusy(false); }
  };

  const fieldClass = (hasError: boolean) =>
    `h-11 w-full rounded-xl border bg-background-50 px-4 text-sm text-foreground-950 outline-none transition-colors placeholder:text-foreground-400 focus:border-accent-400 ${
      hasError ? "border-secondary-500" : "border-background-300"
    }`;

  return (
    <form onSubmit={handleSubmit} className="mt-5 flex flex-col gap-3" noValidate>
      <div>
        <label htmlFor="name" className="mb-1 block font-label text-xs font-semibold text-foreground-700">
          {mode === 'login' ? 'Nombre con el que te registraste' : 'Nombre'}
        </label>
        <div className="relative">
          <i className="ri-user-3-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
          <input
            id="name"
            name="name"
            type="text"
            maxLength={120}
            disabled={busy}
            autoComplete="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Tu nombre"
            className={`${fieldClass(Boolean(errors.name))} pl-11`}
          />
        </div>
        {errors.name ? <p className="mt-1 text-[11px] text-secondary-700">{errors.name}</p> : null}
      </div>

      <div>
        <label htmlFor="phone" className="mb-1 block font-label text-xs font-semibold text-foreground-700">
          Número de celular
        </label>
        <div className="relative">
          <i className="ri-whatsapp-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
          <input
            id="phone"
            name="phone"
            type="tel"
            maxLength={25}
            disabled={busy}
            inputMode="tel"
            autoComplete="tel"
            value={form.phone}
            onChange={handleChange}
            placeholder="55 1234 5678"
            className={`${fieldClass(Boolean(errors.phone))} pl-11`}
          />
        </div>
        {errors.phone ? <p className="mt-1 text-[11px] text-secondary-700">{errors.phone}</p> : null}
      </div>

      {mode === 'register' && <div>
        <label htmlFor="email" className="mb-1 block font-label text-xs font-semibold text-foreground-700">
          Correo
        </label>
        <div className="relative">
          <i className="ri-mail-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
          <input
            id="email"
            name="email"
            type="email"
            maxLength={254}
            disabled={busy}
            autoComplete="email"
            value={form.email}
            onChange={handleChange}
            placeholder="tucorreo@email.com"
            className={`${fieldClass(Boolean(errors.email))} pl-11`}
          />
        </div>
        {errors.email ? <p className="mt-1 text-[11px] text-secondary-700">{errors.email}</p> : null}
      </div>}

      {submitError && <p role="alert" className="rounded-xl bg-secondary-50 p-3 text-xs text-secondary-700">{submitError}</p>}
      <Button type="submit" disabled={busy} variant="primary" size="lg" fullWidth className="mt-1">
        {busy ? 'Conectando…' : mode === 'login' ? 'Iniciar sesión' : 'Crear mi cuenta'}
      </Button>

      <p className="text-center text-[11px] leading-relaxed text-foreground-500">
        {mode === 'login' ? 'Usa el mismo nombre y celular de tu registro. No necesitas volver a crear tu cuenta.' : 'Sin contraseñas. Solo tus datos básicos para agendar.'}
      </p>
    </form>
  );
}
